import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { CompanyControl } from './CompanyControl'

import { useProviderLoader } from '@/Providers/useProviderLoader'
import { ContainerGroupField } from '@/components/common'
import {
    AutocompleteControl,
    FormNavigation,
    RadioGroupControl
} from '@/components/form-controls'
import { useToast } from '@/context/ToastContext'
import mockDropdowns from '@/data/forms/mockDropdowns.json'
import { useAuthHook } from '@/hooks/useAuthHook'
import { useAspirantsApi } from '@/services/api/aspirantsApi'
import { useResourcesApi } from '@/services/api/resourcesApi'
import { useAuthStore } from '@/store/authStore'
import { ErrorHandlingStrategy, MessageLevel } from '@/types/api'
import {
    FormsGroupsProps,
    JobInformationFormData,
    OptionsField
} from '@/types/forms/'
import { useFormProgress } from '@/utils'

// eslint-disable-next-line max-lines-per-function
export const JobInformationForm = ({
    onNext,
    onPrevious,
    isReadOnly = false,
    onBlockedFieldClick
}: FormsGroupsProps): React.ReactElement => {
    const { t } = useTranslation()
    const { control, handleSubmit, reset, watch, setValue } =
        useForm<JobInformationFormData>({
            defaultValues: {
                isWorking: '',
                influenceAttributes: null,
                influenceMedium: null,
                influenceDetail: null,
                companyName: null,
                activity: null,
                kindOfCompany: null,
                businessSector: null,
                workName: null
            }
        })
    const { showToast } = useToast()
    const { idAspirant } = useAuthHook()
    const { idAsesor } = useAuthStore()
    const { showLoader, hideLoader } = useProviderLoader()
    const aspirantsApi = useAspirantsApi({
        messageLevel: MessageLevel.ERRORS,
        errorHandling: ErrorHandlingStrategy.RETURN
    })
    const resourcesApi = useResourcesApi()

    const [razonOptions, setRazonOptions] = useState<OptionsField[]>([])
    const [medioOptions, setMedioOptions] = useState<OptionsField[]>([])
    const [cavOptions, setCavOptions] = useState<OptionsField[]>([])
    const dependentFields = [
        'companyName',
        'activity',
        'kindOfCompany',
        'businessSector',
        'workName'
    ]

    const progress = useFormProgress<JobInformationFormData>({
        watch,
        excludedFields: ['id', 'idAspirant', 'createdAt', 'updatedAt'],
        conditionalFields: [
            {
                field: dependentFields[0],
                dependsOn: 'isWorking',
                condition: 'true'
            },
            {
                field: dependentFields[1],
                dependsOn: 'isWorking',
                condition: 'true'
            },
            {
                field: dependentFields[2],
                dependsOn: 'isWorking',
                condition: 'true'
            },
            {
                field: dependentFields[3],
                dependsOn: 'isWorking',
                condition: 'true'
            },
            {
                field: dependentFields[4],
                dependsOn: 'isWorking',
                condition: 'true'
            },
            {
                field: 'influenceDetail',
                dependsOn: 'influenceMedium',
                condition: '13'
            }
        ]
    })

    useEffect(() => {
        const loadBasicData = async (): Promise<Element | void> => {
            if (!idAspirant) {
                return
            }
            showLoader()

            try {
                // Fetch Job Info and Survey Data in parallel
                const [jobInfoResponse, surveyResponse] =
                    await Promise.allSettled([
                        aspirantsApi.getJobInformationData(idAspirant),
                        aspirantsApi.getEncuestaData(idAspirant)
                    ])

                // Handle Job Info Response
                if (jobInfoResponse.status !== 'fulfilled') {
                    return
                }

                const { data, status } = jobInfoResponse.value

                if (status !== 200) {
                    return
                }

                const responseFormatted = {
                    ...data,
                    isWorking: data.isWorking?.toString()
                }

                // Handle Survey Response
                let surveyData: Partial<JobInformationFormData> = {
                    influenceAttributes: null,
                    influenceMedium: null,
                    influenceDetail: null
                }

                if (
                    surveyResponse.status === 'fulfilled' &&
                    surveyResponse.value.status === 200 &&
                    surveyResponse.value.data
                ) {
                    const sData = surveyResponse.value.data

                    surveyData = {
                        influenceAttributes: sData.zzAspectos,
                        influenceMedium: sData.zzMedio,
                        influenceDetail: sData.zzDetMedio
                    }
                }

                reset({
                    ...responseFormatted,
                    ...surveyData
                })
            } catch (error: any) {
                // Solo mostrar error si no es un 404 (datos no encontrados)
                // Un 404 es normal cuando el aspirante no ha llenado el formulario
                if (error?.status !== 404) {
                    console.error('Error fetching data:', error)
                }
            } finally {
                hideLoader()
            }
        }

        loadBasicData()
    }, [idAspirant, reset])

    useEffect(() => {
        const fetchOptions = async () => {
            const [razonRes, medioRes, cavRes] = await Promise.all([
                resourcesApi.getSapRazonList(),
                resourcesApi.getSapMedioList(),
                resourcesApi.getSapCavList()
            ])

            if (razonRes?.data) {
                setRazonOptions(
                    razonRes.data
                        .filter((item: any) => item.label && item.value && item.label.trim() !== '' && item.label.toLowerCase() !== 'unknown')
                        .map((item: any) => ({
                            label: item.label,
                            value: item.value
                        }))
                )
            }

            if (medioRes?.data) {
                setMedioOptions(
                    medioRes.data
                        .filter((item: any) => item.label && item.value && item.label.trim() !== '' && item.label.toLowerCase() !== 'unknown')
                        .map((item: any) => ({
                            label: item.label,
                            value: item.value
                        }))
                )
            }
            if (cavRes?.data) {
                setCavOptions(
                    cavRes.data
                        .filter((item: any) => item.label && item.value && item.label.trim() !== '' && item.label.toLowerCase() !== 'unknown')
                        .map((item: any) => ({
                            label: item.label,
                            value: item.value
                        }))
                )
            }
        }

        fetchOptions()
    }, [])

    useEffect(() => {
        if (watch('isWorking') === 'true' && watch('companyName') === null) {
            dependentFields.forEach((field) => {
                setValue(field as keyof JobInformationFormData, null)
            })
        }
        if (watch('isWorking') === 'false') {
            dependentFields.forEach((field) => {
                setValue(field as keyof JobInformationFormData, null)
            })
        }
    }, [watch('isWorking')])

    const onSubmit: SubmitHandler<JobInformationFormData> = async (
        data: JobInformationFormData
    ) => {
        if (!idAspirant) {
            return
        }
        showLoader()
        if (data.isWorking === 'false') {
            data.companyName = undefined
            data.activity = undefined
            data.kindOfCompany = undefined
            data.businessSector = undefined
            data.workName = undefined
        }

        try {
            // Save Job Info
            const responseJob = await aspirantsApi.postJobInformationData(
                idAspirant,
                data,
                idAsesor || 0
            )

            // Save Survey Data
            // We only try to save if we have the values? Or always?
            // The fields are required in the form, so we should save them.
            if (data.influenceAttributes && data.influenceMedium) {
                await aspirantsApi.saveEncuestaData(
                    idAspirant,
                    {
                        idAspirant,
                        zzAspectos: data.influenceAttributes,
                        zzMedio: data.influenceMedium,
                        zzDetMedio: data.influenceDetail || ''
                    },
                    idAsesor || 0
                )
            }

            if (responseJob.status === 200) {
                showToast({
                    title: t('basicDataInformation.title'),
                    message: t('forms.ui.save'),
                    type: 'success'
                })

                onNext()
            }
        } catch (error) {
            console.error('Error saving data:', error)
            showToast({
                title: t('basicDataInformation.title'), // You might want a generic error title
                message: t('forms.ui.error') || 'Error al guardar',
                type: 'error'
            })
        } finally {
            hideLoader()
        }
    }

    return (
        <div className={`relative ${isReadOnly ? 'opacity-60' : ''}`}>
            {isReadOnly && (
                <>
                    <div className="absolute inset-0 z-10 bg-gray-200 bg-opacity-50 cursor-not-allowed" />
                    <div
                        className="absolute inset-0 z-20 bg-transparent cursor-pointer"
                        onClick={onBlockedFieldClick}
                    />
                    <div className="absolute top-2 right-2 z-30 bg-yellow-100 border border-yellow-400 text-yellow-800 px-2 py-1 rounded text-xs">
                        🔒 Campos bloqueados - Click para más información
                    </div>
                </>
            )}
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="w-full space-y-4"
            >
                <ContainerGroupField
                    tittleGroup={t('jobInformation.title')}
                    onlyOneField={true}
                >
                    <RadioGroupControl
                        name="isWorking"
                        control={control}
                        label={t('jobInformation.isWorking')}
                        options={mockDropdowns.optionsBoolean}
                        rules={{
                            required: t(
                                'basicDataInformation.isWorkingRequired'
                            )
                        }}
                    />
                </ContainerGroupField>

                {watch('isWorking') === 'true' && (
                    <CompanyControl control={control} />
                )}

                <ContainerGroupField
                    tittleGroup={t('jobInformation.survey')}
                    onlyOneField={true}
                    className="gap-5"
                >
                    <AutocompleteControl
                        name="influenceAttributes"
                        control={control}
                        label={t('jobInformation.influenceAttributes')}
                        options={razonOptions}
                        rules={{
                            required: t(
                                'jobInformation.required.influenceAttributesRequired'
                            )
                        }}
                        listboxProps={{
                            itemClasses: {
                                base: 'whitespace-normal h-auto',
                                title: 'whitespace-normal'
                            }
                        }}
                    />

                    <AutocompleteControl
                        name="influenceMedium"
                        control={control}
                        label={t('jobInformation.influenceMedium')}
                        options={medioOptions}
                        rules={{
                            required: t(
                                'jobInformation.required.influenceMediumRequired'
                            )
                        }}
                        listboxProps={{
                            itemClasses: {
                                base: 'whitespace-normal h-auto',
                                title: 'whitespace-normal'
                            }
                        }}
                    />

                    {watch('influenceMedium') === '13' && (
                        <AutocompleteControl
                            name="influenceDetail"
                            control={control}
                            label={t('jobInformation.influenceDetail')}
                            options={cavOptions}
                            rules={{
                                required: t(
                                    'jobInformation.required.influenceDetailRequired'
                                )
                            }}
                            listboxProps={{
                                itemClasses: {
                                    base: 'whitespace-normal h-auto',
                                    title: 'whitespace-normal'
                                }
                            }}
                        />
                    )}
                </ContainerGroupField>

                <FormNavigation
                    onSubmit={handleSubmit(onSubmit)}
                    onPrevious={onPrevious}
                    progress={progress}
                    submitLabel={t('forms.ui.nextPage')}
                />
            </form>
        </div>
    )
}
